import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MultilangText, ServiceField } from "../../models/Service";

// What this renderer can actually draw. CORE declares a fourth value, QRCODE,
// which needs a scanner this portal does not have -- so it is deliberately
// absent here and takes the unsupported path with anything CORE adds later.
const RENDERABLE_INPUTS = ["NUMERICAL", "ALPHA_CS", "ALPHA_CI"] as const;

// T-16-20-02. An upstream pattern run against payer input is a catastrophic-
// backtracking target. Length is the WRONG axis and a length cap alone is no
// mitigation at all: `^(a+)+$` is seven characters and took 43s on 30 of
// input, measured in this engine. What actually costs is a quantifier applied
// to a group that already contains one, so that is what gets refused.
const MAX_PATTERN_LENGTH = 200;
const MAX_TESTED_LENGTH = 256;

// Star height > 1. Measured: every pattern this refuses ran for tens of
// seconds; every pattern it allows, including the repo's own email regex,
// returned in 0ms. A refusal costs the payer instant feedback on one field
// and never blocks the payment, so erring toward refusing is the safe side.
export const hasNestedQuantifier = (pattern: string): boolean => {
  const quantifiedAtDepth: boolean[] = [];
  let depth = 0;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    if (char === "\\") {
      i++;
      continue;
    }

    if (char === "(") {
      depth++;
      quantifiedAtDepth[depth] = false;
      continue;
    }

    if (char === ")") {
      const innerHadQuantifier = quantifiedAtDepth[depth] === true;
      depth--;
      const next = pattern[i + 1];
      const groupIsQuantified = next === "*" || next === "+" || next === "{";

      if (innerHadQuantifier && groupIsQuantified) return true;
      if (groupIsQuantified && depth >= 1) quantifiedAtDepth[depth] = true;
      continue;
    }

    if ((char === "*" || char === "+" || char === "{") && depth >= 1) {
      quantifiedAtDepth[depth] = true;
    }
  }

  return false;
};

type ConfiguredFieldsProps = {
  fields: ServiceField[];
  values?: Record<string, string>;
  onChange?: (fieldId: string, value: string) => void;
  // onSupported fields cannot be drawn, so the parent can refuse to submit since there no
  // fields to submit this in. 
  onUnsupported?: (blockingFieldIds: string[]) => void;
  // Incomplete fields are mandatory fields that are blank or have a failing regex
  onIncomplete?: (incompleteFieldIds: string[]) => void;
};

// An empty label on a payment form is worse than a technical identifier, so
// this never returns "": exact locale, then any locale CORE sent, then the id.
const resolveText = (
  entries: MultilangText[] | undefined,
  language: string,
  fallback = ""
): string => {
  const exact = entries?.find(e => e.lang === language)?.value;
  if (exact) return exact;

  const any = entries?.find(e => !!e.value)?.value;
  return any || fallback;
};

const isRenderable = (input: string): boolean =>
  (RENDERABLE_INPUTS as readonly string[]).includes(input);


const DELIMITED_PATTERN = /^\/(.*)\/([a-z]*)$/;

// Removes delimited regex if it starts and ends with back-slashes
const unwrapDelimiters = (regex: string): { pattern: string; flags: string } => {
  const delimited = regex.match(DELIMITED_PATTERN);
  return delimited
    ? { pattern: delimited[1], flags: delimited[2] }
    : { pattern: regex, flags: "" };
};

// A pattern that will not compile, or is long enough to be a denial-of-service
// vector, yields no validator rather than an exception or a blocked payer.
const compilePattern = (regex: string): RegExp | null => {
  if (!regex || regex.length > MAX_PATTERN_LENGTH) return null;

  const { pattern, flags } = unwrapDelimiters(regex);
  if (hasNestedQuantifier(pattern)) return null;

  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
};

// The value a field currently holds, the same precedence the render loop
// uses: the caller's own state wins when it has an entry, so a controlled
// `values` prop is authoritative over what has merely been typed locally.
const fieldValue = (
  values: Record<string, string> | undefined,
  edited: Record<string, string>,
  fieldId: string
): string => values?.[fieldId] ?? edited[fieldId] ?? "";

const ConfiguredFields = ({
  fields,
  values,
  onChange,
  onUnsupported,
  onIncomplete,
}: ConfiguredFieldsProps) => {
  const { t, i18n } = useTranslation();
  const [edited, setEdited] = useState<Record<string, string>>({});

  const unsupported = useMemo(
    () =>
      fields.filter(f => f.mandatory && !isRenderable(f.input)).map(f => f.fieldId),
    [fields]
  );

  const incomplete = useMemo(
    () =>
      fields
        .filter(f => {
          if (!f.mandatory || !isRenderable(f.input)) return false;

          const value = fieldValue(values, edited, f.fieldId);
          if (value.trim() === "") return true;

          const pattern = compilePattern(f.regex);
          return !!pattern && !pattern.test(value.slice(0, MAX_TESTED_LENGTH));
        })
        .map(f => f.fieldId),
    [fields, values, edited]
  );

  // Both callbacks are held in refs rather than left out of the dependency
  // arrays. Omitting them stops the loop an inline arrow would cause, but it
  // also pins the FIRST callback forever: a parent whose handler closes over
  // changing state would keep being called with a stale one.
  const onUnsupportedRef = useRef(onUnsupported);
  useEffect(() => {
    onUnsupportedRef.current = onUnsupported;
  });
  const onIncompleteRef = useRef(onIncomplete);
  useEffect(() => {
    onIncompleteRef.current = onIncomplete;
  });

  const unsupportedKey = unsupported.join(",");
  useEffect(() => {
    onUnsupportedRef.current?.(unsupportedKey ? unsupportedKey.split(",") : []);
  }, [unsupportedKey]);

  const incompleteKey = incomplete.join(",");
  useEffect(() => {
    onIncompleteRef.current?.(incompleteKey ? incompleteKey.split(",") : []);
  }, [incompleteKey]);

  if (fields.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {fields.map(field => {
        const label = resolveText(field.label, i18n.language, field.fieldId);
        const hint = resolveText(field.hint, i18n.language);

        if (!isRenderable(field.input)) {
          // Every unsupported field is visible as an alert; a MANDATORY one
          // also says the service cannot be paid without it, so "cannot be
          // shown" and "cannot be paid" stay distinguishable to the payer.
          const notice = `${label} — ${t(
            "configuredFields.unsupported.notice"
          )}`;

          return (
            <Alert
              key={field.fieldId}
              severity={field.mandatory ? "warning" : "info"}
              data-testid={`configured_field_unsupported_${field.fieldId}`}
            >
              {field.mandatory
                ? `${notice} ${t("configuredFields.unsupported.blocking")}`
                : notice}
            </Alert>
          );
        }

        const value = fieldValue(values, edited, field.fieldId);
        const pattern = compilePattern(field.regex);
        const invalid =
          !!pattern &&
          value.length > 0 &&
          !pattern.test(value.slice(0, MAX_TESTED_LENGTH));

        return (
          <TextField
            key={field.fieldId}
            id={`configured_field_${field.fieldId}`}
            data-testid={`configured_field_${field.fieldId}`}
            label={label}
            required={field.mandatory}
            error={invalid}
            helperText={invalid ? t("configuredFields.invalid") : hint}
            value={value}
            // NUMERICAL is a keypad hint, not a filter: the regex CORE sent is
            // what decides validity, and a type=number field would swallow it.
            inputMode={field.input === "NUMERICAL" ? "numeric" : "text"}
            onChange={event => {
              const next = event.target.value;
              setEdited(prev => ({ ...prev, [field.fieldId]: next }));
              onChange?.(field.fieldId, next);
            }}
            fullWidth
            size="small"
          />
        );
      })}
    </Box>
  );
};

export default ConfiguredFields;
